import { jsx } from 'react/jsx-runtime';
import { useContext, createContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useRoutePParamChange } from '../../ExpandedResource/hooks/useRoutePParamChange.js';
import { usePDFSearch } from './usePDFSearch.js';
import { RenderingCancelledException } from 'pdfjs-dist';
import pLimit from 'p-limit';
import { isCommentWithTextAnnotation } from '../../../../comments/utils/annotationGuards.js';
import { isDefined, isTruthy } from '@fabric-app/utils/guards';
import { useSelectionBasedAnnotations } from '../../../../annotation/hooks/useSelectionBasedAnnotations.js';
import { SEARCH_REFERENCE_TEXT_LENGTH } from '../../../../annotation/config.js';

const PDFContentContext = /* @__PURE__ */ createContext({
  pdf: void 0,
  currentPage: 1,
  setCurrentPage: () => {
  },
  updateCurrentPage: () => {
  },
  totalPages: 0,
  isReady: false,
  getEstimatedHeight: () => 0,
  getPagePlaceholderImage: () => null,
  tinyPagesVisible: false,
  scale: 1,
  scrollToPage: () => {
  },
  tinyPagesRef: null,
  setTinyPagesRef: () => {
  },
  scrollToTinyPage: () => {
  },
  contentRef: {
    current: null
  },
  textItemPositions: {},
  textItemMatchData: {},
  pagesTextContent: [],
  activeMatch: null,
  activeMatchId: null,
  matchIndexMapping: {},
  commentMap: /* @__PURE__ */ new Map(),
  resourceId: void 0,
  domModel: null,
  onAnnotationCreation: () => {
  },
  onHighlightCreation: () => {
  },
  fullEstimatedHeight: 0
});
const usePDFContent = () => useContext(PDFContentContext);
const scale = 1;
const PDFContentProvider = ({ pdf, children, tinyPagesVisible, contentRef, currentPage, setCurrentPage, comments = [], resourceId, width }) => {
  const [baseViewportWidths, setBaseViewportWidths] = useState([]);
  const [baseHeights, setBaseHeights] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const [pagePlaceholderImages, setPagePlaceholderImages] = useState({});
  const [totalPages, setTotalPages] = useState(0);
  const [tinyPagesRef, setTinyPagesRef] = useState(null);
  const scrollToTinyPage = useCallback((page) => {
    if (!tinyPagesRef) return;
    const pageElement = tinyPagesRef.querySelector(`[data-page="${page}"]`);
    if (!pageElement) return;
    pageElement.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center"
    });
  }, [
    tinyPagesRef
  ]);
  const scrollToPage = useCallback((page, instant = false) => {
    if (!contentRef?.current) return;
    const pageElement = contentRef.current.querySelector(`[data-page-index="${page}"]`);
    if (!pageElement) return;
    pageElement.scrollIntoView({
      behavior: instant ? "instant" : "smooth"
    });
    scrollToTinyPage(page);
  }, [
    contentRef,
    scrollToTinyPage
  ]);
  const { textItemPositions, textItemMatchData, pagesTextContent, activeMatch, activeMatchId, matchIndexMapping, searchQueryHash } = usePDFSearch({
    pdf,
    currentPage,
    scrollToPage
  });
  useRoutePParamChange({
    onParamChange: (p) => {
      scrollToPage(p);
    },
    enabled: isReady
  });
  useEffect(() => {
    if (!pdf) return;
    const calculate = async () => {
      const viewportWidths = [];
      const heights = [];
      for (let i = 0; i < pdf.numPages; i++) {
        try {
          const page = await pdf.getPage(i + 1);
          const viewport = page.getViewport({
            scale: 1
          });
          viewportWidths.push(viewport.width);
          heights.push(viewport.height);
        } catch (error) {
          console.error("Error", error);
          continue;
        }
      }
      setBaseViewportWidths(viewportWidths);
      setBaseHeights(heights);
      setIsReady(true);
    };
    const limit = pLimit(3);
    for (let i = 1; i <= pdf.numPages; i++) {
      limit(async () => {
        const res = await generatePlaceholderImage({
          pdf,
          pageNumber: i
        });
        if (res) {
          setPagePlaceholderImages((prev) => ({
            ...prev,
            [i]: res
          }));
        }
      });
    }
    calculate();
  }, [
    pdf
  ]);
  const getPagePlaceholderImage = useCallback((pageNumber) => {
    if (!isReady || !pdf) return null;
    const placeholderImage = pagePlaceholderImages[pageNumber];
    return placeholderImage;
  }, [
    pagePlaceholderImages,
    isReady,
    pdf
  ]);
  const getEstimatedHeight = useCallback((pageNumber, width2, ignoreScale = false) => {
    if (!pdf || !isReady) return 1;
    const baseWidth = baseViewportWidths[pageNumber - 1] ?? 1 * (ignoreScale ? 1 : scale);
    const baseHeight = baseHeights[pageNumber - 1] ?? 1 * (ignoreScale ? 1 : scale);
    const widthScale = width2 / baseWidth;
    return baseHeight * widthScale;
  }, [
    pdf,
    baseViewportWidths,
    baseHeights,
    isReady
  ]);
  const fullEstimatedHeight = useMemo(() => {
    if (!pdf || !isReady) return 1;
    let height = 0;
    for (let i = 0; i < pdf.numPages; i++) {
      height += getEstimatedHeight(i + 1, width, true);
    }
    return height;
  }, [
    getEstimatedHeight,
    pdf,
    isReady,
    width
  ]);
  useEffect(() => {
    setTotalPages(pdf?.numPages ?? 0);
  }, [
    pdf,
    setTotalPages
  ]);
  const updateCurrentPage = useCallback(() => {
    if (!contentRef?.current) return;
    const pageElements = contentRef.current.querySelectorAll("[data-page-index]");
    if (!pageElements.length) return;
    let mostVisiblePage = null;
    let highestVisibility = 0;
    pageElements.forEach((element) => {
      const pageNumber = parseInt(element.getAttribute("data-page-index") || "0", 10);
      if (!pageNumber) return;
      const rect = element.getBoundingClientRect();
      const viewportHeight = contentRef.current?.clientHeight ?? 0;
      const viewportWidth = contentRef.current?.clientWidth ?? 0;
      const top = Math.max(0, rect.top);
      const bottom = Math.min(viewportHeight, rect.bottom);
      const left = Math.max(0, rect.left);
      const right = Math.min(viewportWidth, rect.right);
      if (top >= bottom || left >= right) return;
      const visibleHeight = bottom - top;
      const visibleWidth = right - left;
      const visibleArea = visibleHeight * visibleWidth;
      const totalArea = rect.width * rect.height;
      const visibilityPercentage = totalArea > 0 ? visibleArea / totalArea : 0;
      if (visibilityPercentage > highestVisibility) {
        highestVisibility = visibilityPercentage;
        mostVisiblePage = pageNumber;
      }
    });
    if (mostVisiblePage) {
      setCurrentPage((currentPage2) => {
        if (currentPage2 === mostVisiblePage) return currentPage2;
        scrollToTinyPage(mostVisiblePage);
        return mostVisiblePage;
      });
    }
  }, [
    contentRef,
    scrollToTinyPage,
    setCurrentPage
  ]);
  const commentMap = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    for (const comment of comments) {
      if (!isCommentWithTextAnnotation(comment) || !isDefined(comment.annotation.text.chunkSize) || !isDefined(comment.annotation.text.startChunk) || !isDefined(comment.annotation.text.endChunk)) continue;
      for (let i = comment.annotation.text.startChunk; i <= comment.annotation.text.endChunk; i++) {
        map.set(i, [
          ...map.get(i) ?? [],
          comment
        ]);
      }
    }
    return map;
  }, [
    comments
  ]);
  const { model, onAnnotationCreation, onHighlightCreation } = useSelectionBasedAnnotations({
    doc: contentRef.current,
    enabled: isReady,
    resourceId,
    domModelOptions: {
      mode: "dynamic",
      totalChunks: totalPages,
      chunkAttribute: "data-page-number",
      chunkFindSelector: ".react-pdf__Page[data-page-number]",
      chunkSelectorForTextNode: ".react-pdf__Page__textContent",
      checkItemPositionsForSegmentSpacing: true
    },
    scrollToChunk: (chunkIndex) => {
      scrollToPage(chunkIndex + 1, true);
    }
  });
  useEffect(() => {
    if (!model) return;
    const timeout = window.setTimeout(() => {
      const searchWidgets = Object.entries(textItemMatchData).flatMap(([page, matches]) => {
        return Object.entries(matches).flatMap(([itemId, match]) => {
          const pageNumber = parseInt(page, 10);
          const itemNumber = parseInt(itemId, 10);
          const pageData = textItemPositions[pageNumber];
          const item = pageData.items[itemNumber];
          if (!item || !match.groupId) return null;
          const widgets = [];
          const initialGroupId = match.groupId - (match.ranges.length - 1);
          for (const [index, range] of Object.entries(match.ranges)) {
            const matchStart = range[0];
            const matchEnd = range[1];
            const start = item.start + matchStart;
            const end = item.start + matchEnd;
            const id = `search-${initialGroupId + parseInt(index, 10)}-${searchQueryHash}`;
            widgets.push({
              id,
              startChunk: pageNumber - 1,
              endChunk: pageNumber - 1,
              locationStart: start,
              locationEnd: end,
              attributes: {
                "data-search": "true",
                "data-search-id": (value2) => value2.trim() === "" ? id : `${value2},${id}`
              },
              text: pageData.fullText.substring(start, end),
              textBefore: pageData.fullText.substring(Math.max(0, start - SEARCH_REFERENCE_TEXT_LENGTH), start),
              textAfter: pageData.fullText.substring(end, Math.min(end + SEARCH_REFERENCE_TEXT_LENGTH, pageData.fullText.length)),
              meta: {
                isSearch: true
              }
            });
          }
          return widgets;
        });
      }).filter(isTruthy);
      model.setWidgets((widgets) => [
        ...widgets.filter((w) => !w.meta?.isSearch),
        ...searchWidgets
      ]);
    }, 300);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [
    model,
    textItemMatchData,
    textItemPositions,
    searchQueryHash
  ]);
  const value = useMemo(() => ({
    pdf,
    currentPage,
    getEstimatedHeight,
    updateCurrentPage,
    isReady,
    getPagePlaceholderImage,
    tinyPagesVisible: tinyPagesVisible ?? false,
    scale,
    scrollToPage,
    totalPages,
    setCurrentPage,
    tinyPagesRef,
    setTinyPagesRef,
    scrollToTinyPage,
    contentRef,
    textItemPositions,
    textItemMatchData,
    pagesTextContent,
    activeMatch,
    activeMatchId,
    matchIndexMapping,
    commentMap,
    resourceId,
    domModel: model,
    onAnnotationCreation,
    onHighlightCreation,
    fullEstimatedHeight
  }), [
    pdf,
    currentPage,
    getEstimatedHeight,
    updateCurrentPage,
    isReady,
    getPagePlaceholderImage,
    tinyPagesVisible,
    scrollToPage,
    totalPages,
    setCurrentPage,
    tinyPagesRef,
    setTinyPagesRef,
    scrollToTinyPage,
    contentRef,
    textItemPositions,
    textItemMatchData,
    pagesTextContent,
    activeMatch,
    activeMatchId,
    matchIndexMapping,
    commentMap,
    resourceId,
    model,
    onAnnotationCreation,
    onHighlightCreation,
    fullEstimatedHeight
  ]);
  return /* @__PURE__ */ jsx(PDFContentContext.Provider, {
    value,
    children
  });
};
const canvasToBase64 = async (canvas) => {
  return canvas.toDataURL("image/jpg", 1);
};
const generatePlaceholderImage = async ({ pdf, pageNumber, onError }) => {
  try {
    const page = await pdf.getPage(pageNumber);
    const normalViewport = page.getViewport({
      scale: 2
    });
    const canvas = document.createElement("canvas");
    canvas.width = normalViewport.width;
    canvas.height = normalViewport.height;
    await page.render({
      canvasContext: canvas.getContext("2d"),
      viewport: normalViewport
    }).promise;
    const x1 = await canvasToBase64(canvas);
    canvas.remove();
    return {
      normal: x1
    };
  } catch (error) {
    if (error instanceof RenderingCancelledException) {
      return null;
    }
    console.error("Error", error);
    onError?.(error);
  }
  return null;
};

export { PDFContentProvider as default, usePDFContent };
//# sourceMappingURL=PDF.js.map
